import { ArrowRight, Eye, EyeOff, House, Lock, Shield, User } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '../../assets/scss/pages/AdminLogin.module.scss';
import loginBg from '../../assets/img/poter_login.png';
import logo from '../../assets/img/logo.png';
import api from '../../config/axios-conf';
import { version } from '../../config/version-code';

export default function SuperAdminLoginPage() {
    const navigate = useNavigate();

    const [formData, setFormData] = useState({ userName: '', passWord: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [bgLoaded, setBgLoaded] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem('token');
        const user = localStorage.getItem('user');
        if (token && user) {
            try {
                const userData = JSON.parse(user);
                if (userData.role === 'superadmin') {
                    navigate('/navo-admin/dashboard', { replace: true });
                }
            } catch {
                // ignore
            }
        }
    }, [navigate]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (error) setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        const userName = formData.userName.trim();
        const passWord = formData.passWord.trim();

        if (!userName || !passWord) {
            setError('Vui lòng nhập đầy đủ thông tin');
            return;
        }

        setIsLoading(true);

        try {
            const { data } = await api.post('/auth/login', { userName, passWord });

            if (data.user.role !== 'superadmin') {
                setError('Chỉ có superadmin mới được đăng nhập vào hệ thống này');
                return;
            }

            localStorage.setItem('token', data.access_token);
            localStorage.setItem('refreshToken', data.refresh_token);
            localStorage.setItem('user', JSON.stringify(data.user));
            if (data.refresh_token_expires_at) {
                localStorage.setItem('refreshTokenExpiresAt', String(data.refresh_token_expires_at));
            }

            navigate('/navo-admin/dashboard', { replace: true });
        } catch (err) {
            const msg = err.response?.data?.message;
            // Ưu tiên message từ backend (vd: tài khoản bị khoá / hết hạn) thay vì
            // ghi đè cứng theo status — backend trả cùng 401 cho mọi trường hợp.
            setError(msg || 'Tên đăng nhập hoặc mật khẩu không đúng');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={styles.adminLogin}>
            {/* ===== LEFT PANEL ===== */}
            <div className={styles.adminLoginLeft}>
                {!bgLoaded && <div className={styles.adminLoginBgSkeleton} />}
                <img src={loginBg} alt="Navo Sổ Tiệm" className={styles.adminLoginBgImage} onLoad={() => setBgLoaded(true)} />
                <div className={styles.adminLoginBgOverlay} />

                <div className={styles.adminLoginLeftContent}>
                    <div className={styles.adminLoginHero} />
                    <div className={styles.adminLoginFooter}>
                        &copy; 2025 Navo Sổ Tiệm. Phiên bản: {version}
                    </div>
                </div>
            </div>

            {/* ===== RIGHT PANEL ===== */}
            <div className={styles.adminLoginRight}>
                <div className={styles.adminLoginBrand}>
                    <img src={logo} alt="Navo logo" className={styles.adminLoginBrandLogo} />
                    <span className={styles.adminLoginBrandName}>Navo Sổ Tiệm</span>
                </div>
                <button className={styles.adminLoginHomeBtn} onClick={() => navigate('/')}>
                    <House size={16} /> Trang chủ
                </button>
                <div className={styles.adminLoginFormWrapper}>
                    <h2 className={styles.adminLoginTitle}>
                        <Shield size={24} style={{ verticalAlign: 'middle', marginRight: 8 }} />
                        Super Admin
                    </h2>
                    <p className={styles.adminLoginSubtitle}>Đăng nhập dành cho quản trị viên hệ thống Navo</p>

                    <form onSubmit={handleSubmit} className={styles.adminLoginForm}>
                        {/* Tên đăng nhập */}
                        <div className={styles.adminLoginField}>
                            <label className={styles.adminLoginLabel}>TÊN ĐĂNG NHẬP</label>
                            <div className={styles.adminLoginInputWrapper}>
                                <span className={styles.adminLoginInputIcon}><User /></span>
                                <input
                                    type="text"
                                    name="userName"
                                    value={formData.userName}
                                    onChange={handleInputChange}
                                    className={styles.adminLoginInput}
                                    placeholder="Nhập tên đăng nhập"
                                    autoComplete="username"
                                />
                            </div>
                        </div>

                        {/* Mật khẩu */}
                        <div className={styles.adminLoginField}>
                            <label className={styles.adminLoginLabel}>MẬT KHẨU</label>
                            <div className={styles.adminLoginInputWrapper}>
                                <span className={styles.adminLoginInputIcon}><Lock /></span>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    name="passWord"
                                    value={formData.passWord}
                                    onChange={handleInputChange}
                                    className={styles.adminLoginInput}
                                    placeholder="Nhập mật khẩu"
                                    autoComplete="current-password"
                                />
                                <button
                                    type="button"
                                    className={styles.adminLoginTogglePassword}
                                    onClick={() => setShowPassword(!showPassword)}
                                    tabIndex={-1}
                                >
                                    {showPassword ? <EyeOff /> : <Eye />}
                                </button>
                            </div>
                        </div>

                        {error && <div className={styles.adminLoginError}>{error}</div>}

                        <button type="submit" className={styles.adminLoginSubmitBtn} disabled={isLoading}>
                            {isLoading ? 'Đang đăng nhập...' : 'Đăng nhập'}
                            {!isLoading && <ArrowRight />}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
